import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import billingApi, { startPlanCheckout } from '../services/billingApi';
import { formatApiError } from '../utils/errorMessage';
import ErrorBoundary from '../components/ErrorBoundary';
import { readPlanChoice, clearPlanChoice } from '../features/plans/planChoice';

const PLAN_INCLUDED = {
  personal: [
    'Track personal income and expenses',
    'Create and manage budgets',
    'Monitor multiple accounts',
    'Track investments and assets',
    'Plan for financial goals',
    'One location',
    '15-day free trial for your first plan'
  ],
  basic: [
    'Professional invoicing with your logo',
    'Accounts receivable tracking',
    'Payroll and staff management',
    'Asset and liability tracking',
    'Comprehensive financial reports',
    'One location'
  ],
  premium: [
    'Professional invoicing with your logo',
    'Accounts receivable tracking',
    'Payroll and staff management',
    'Asset and liability tracking',
    'Comprehensive financial reports',
    'One location included',
    'Each extra location is $5.00 a month or $50.00 a year'
  ],
  'extra-location': [
    'Adds this location to your Premium account'
  ]
};

const LOCATION_PLANS = ['personal', 'basic', 'premium'];

const STATUS_LABELS = {
  trial: 'Free trial',
  active: 'Active',
  payment_failed: 'Payment failed',
  incomplete: 'No plan yet',
  cancelled: 'Ended',
  expired: 'Ended',
  suspended: 'Suspended'
};

function centsOf(amount) {
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(String(amount ?? ''));
  if (!match) return null;
  return Number(match[1]) * 100 + Number((match[2] || '0').padEnd(2, '0'));
}

function yearlyCents(plan, cycle) {
  if (!plan) return null;
  const cents = centsOf(cycle === 'annual' ? plan.annualPrice : plan.monthlyPrice);
  if (cents === null) return null;
  return cycle === 'annual' ? cents : cents * 12;
}

function formatDate(value) {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString();
}

function apiError(e, fallback) {
  return formatApiError(e, fallback);
}

function planSummary(subscription, planName) {
  const status = subscription.status;
  if (subscription.access?.open === false) {
    if (subscription.access.reason === 'trial_ended') return 'Your free trial has ended. Choose a plan to open this location again.';
    if (status === 'incomplete') return 'Choose a plan below to open this location. A card is required.';
    return 'This plan has ended. Choose a plan to open this location again.';
  }
  if (status === 'trial') {
    const ends = formatDate(subscription.trial_ends_at);
    if (subscription.cancel_at_period_end) return `${planName} free trial ends ${ends}. It is cancelled, so you will not be charged.`;
    if (!subscription.stripe_subscription_id) return `${planName} free trial ends ${ends}. Add a card with a plan below to keep access after that.`;
    return `${planName} free trial ends ${ends}. Your card is charged then unless you cancel before that date.`;
  }
  if (status === 'payment_failed') {
    return 'Your last payment did not go through. Update your card under Manage billing to keep this location open.';
  }
  const periodEnd = formatDate(subscription.current_period_end);
  if (subscription.cancel_at_period_end) return `${planName} is cancelled and stays open until ${periodEnd}.`;
  return periodEnd ? `${planName} renews ${periodEnd}.` : `${planName} is active.`;
}

const Billing = ({ selectedBusiness }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [billingCycle, setBillingCycle] = useState(() => {
    const fromQuery = searchParams.get('cycle');
    if (fromQuery === 'annual' || fromQuery === 'monthly') return fromQuery;
    return readPlanChoice()?.cycle || 'monthly';
  });
  const [suggestedPlan] = useState(() => searchParams.get('plan') || readPlanChoice()?.plan || null);
  const [busy, setBusy] = useState('');
  const [confirming, setConfirming] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [plansRes, subRes] = await Promise.all([
        billingApi.getPlans(),
        billingApi.getSubscription(selectedBusiness),
      ]);
      setPlans(plansRes.data);
      setSubscription(subRes.data);
      setError('');
    } catch (e) {
      setError(apiError(e, 'Failed to load billing information'));
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness]);

  useEffect(() => {
    if (!selectedBusiness) return;
    const checkout = searchParams.get('checkout');
    const sessionId = searchParams.get('session_id');
    const clearCheckoutParams = () => {
      const next = new URLSearchParams(searchParams);
      ['checkout', 'session_id', 'plan', 'cycle'].forEach((key) => next.delete(key));
      setSearchParams(next, { replace: true });
    };
    if (checkout === 'success' && sessionId) {
      clearPlanChoice();
      billingApi.completeCheckout({ business_id: selectedBusiness, session_id: sessionId })
        .then((res) => {
          setSubscription(res.data);
          setNotice(res.data?.access?.open
            ? 'Your plan is active. Thank you!'
            : 'Payment received. Your plan opens in a moment; refresh this page if it does not.');
        })
        .catch(() => setNotice('Payment received. Your plan opens in a moment; refresh this page if it does not.'))
        .finally(() => {
          clearCheckoutParams();
          loadData();
        });
      return;
    }
    if (checkout === 'cancelled') {
      setNotice('Checkout was cancelled. No charge was made.');
      clearCheckoutParams();
    }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusiness]);

  const accessOpen = subscription?.access?.open;
  useEffect(() => {
    if (accessOpen !== undefined) window.dispatchEvent(new Event('billing-updated'));
  }, [accessOpen]);

  const runAction = async (key, action, fallback) => {
    setBusy(key);
    setError('');
    setNotice('');
    try {
      await action();
    } catch (e) {
      setError(apiError(e, fallback));
    } finally {
      setBusy('');
    }
  };

  const startPlan = (planKey) => runAction(`start-${planKey}`, async () => {
    const result = await startPlanCheckout({ businessId: selectedBusiness, plan: planKey, billingCycle });
    if (!result.redirected) {
      clearPlanChoice();
      await loadData();
    }
  }, 'Failed to start checkout');

  const changePlan = (planKey) => runAction(`change-${planKey}`, async () => {
    const res = await billingApi.changePlan({ business_id: selectedBusiness, plan: planKey, billing_cycle: billingCycle });
    setSubscription(res.data);
    setConfirming(null);
    setNotice('Your plan was changed.');
  }, 'Failed to change plan');

  const cancelPlan = () => runAction('cancel', async () => {
    const res = await billingApi.cancel({ business_id: selectedBusiness });
    setSubscription(res.data);
    setConfirming(null);
    setNotice(res.data.status === 'trial'
      ? 'Your trial is cancelled. You will not be charged.'
      : 'Your plan is cancelled and stays open until the end of the paid period.');
  }, 'Failed to cancel the plan');

  const resumePlan = () => runAction('resume', async () => {
    const res = await billingApi.resume({ business_id: selectedBusiness });
    setSubscription(res.data);
    setNotice('Your plan will continue.');
  }, 'Failed to keep the plan');

  const openPortal = () => runAction('portal', async () => {
    const res = await billingApi.openPortal({ business_id: selectedBusiness });
    if (res.data?.url) window.location.assign(res.data.url);
  }, 'Failed to open billing');

  if (!selectedBusiness) {
    return (
      <ErrorBoundary section="Billing">
        <div className="p-6 text-gray-700">Add a person or business first. This page is the payment for eloNest, not that location's bills.</div>
      </ErrorBoundary>
    );
  }

  const plansByKey = Object.fromEntries(plans.map((plan) => [plan.key, plan]));
  const currentPlan = subscription ? plansByKey[subscription.plan_name] : null;
  const hasOpenPlan = Boolean(subscription?.access?.open) && subscription?.plan_name !== 'unset';
  const canChange = Boolean(subscription?.can_change_plan);
  const isExtraLocation = subscription?.plan_name === 'extra-location';
  const pendingCancel = Boolean(subscription?.cancel_at_period_end);
  const visiblePlanKeys = [
    ...LOCATION_PLANS,
    ...((subscription?.extra_location_eligible || isExtraLocation) ? ['extra-location'] : [])
  ].filter((key) => plansByKey[key]);

  const describeChange = (planKey) => {
    const target = plansByKey[planKey];
    const from = yearlyCents(currentPlan, subscription?.billing_cycle);
    const to = yearlyCents(target, billingCycle);
    const upgrade = from === null || to > from;
    const endsTrial = subscription?.status === 'trial' && !target?.trialDays;
    if (endsTrial) return { upgrade: true, text: `Your free trial ends now and your card is charged for ${target.name} today.` };
    if (upgrade) return { upgrade: true, text: `You are charged the prorated difference for ${target.name} today.` };
    return { upgrade: false, text: `${target.name} starts now. The unused part of your current plan is credited to your next bill.` };
  };

  const planButton = (planKey) => {
    const plan = plansByKey[planKey];
    const price = billingCycle === 'annual' ? plan.annualPrice : plan.monthlyPrice;
    if (!price) return { label: 'Not available', disabled: true };
    const isCurrent = subscription?.plan_name === planKey && subscription?.billing_cycle === billingCycle;
    if (hasOpenPlan && isCurrent) return { label: 'Current plan', disabled: true };
    if (hasOpenPlan && canChange && !isExtraLocation && planKey !== 'extra-location') {
      const change = describeChange(planKey);
      const samePlan = subscription?.plan_name === planKey;
      const label = samePlan
        ? `Switch to ${billingCycle === 'annual' ? 'annual' : 'monthly'}`
        : (change.upgrade ? `Upgrade to ${plan.name}` : `Downgrade to ${plan.name}`);
      return { label, onClick: () => setConfirming({ planKey, ...change }) };
    }
    if (hasOpenPlan && subscription?.live_subscription) return { label: 'Not available for this location', disabled: true };
    const startsTrial = plan.trialDays && subscription?.trial_eligible;
    return {
      label: startsTrial ? `Start ${plan.trialDays}-day free trial` : `Choose ${plan.name}`,
      onClick: () => startPlan(planKey)
    };
  };

  return (
    <ErrorBoundary section="Billing">
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">eloNest plan</h2>
          <p className="text-gray-700 mt-2">This page is the payment for eloNest. It is not the bills, budgets, or repeating charges you track for a person or business. Those stay under Subscriptions and Recurring Bills.</p>
          <p className="text-gray-700 mt-2">Each location has its own plan. A card is required to start any plan; Personal includes a 15-day free trial the first time, and you are not charged if you cancel before it ends.</p>
        </div>

        {error && (
          <div className="p-3 bg-red-50 text-red-700 border border-red-200 rounded" role="alert">{error}</div>
        )}
        {notice && (
          <div className="p-3 bg-green-50 text-green-800 border border-green-200 rounded">{notice}</div>
        )}

        {loading ? (
          <div className="p-6">Loading...</div>
        ) : (
          <>
            {subscription && (
              <div className="bg-white rounded shadow p-6">
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="text-lg font-semibold">
                    {currentPlan && subscription.plan_name !== 'unset' ? currentPlan.name : 'No plan yet'}
                  </h3>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    subscription.access?.open
                      ? (subscription.status === 'payment_failed' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800')
                      : 'bg-gray-200 text-gray-800'
                  }`}>
                    {subscription.access?.reason === 'trial_ended' ? 'Trial ended' : (STATUS_LABELS[subscription.status] || subscription.status)}
                  </span>
                  {pendingCancel && subscription.access?.open && (
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">Cancelled</span>
                  )}
                </div>
                <p className="text-gray-700 mt-2">{planSummary(subscription, currentPlan?.name || 'Your plan')}</p>
                {subscription.checkout_pending && !subscription.access?.open && (
                  <p className="text-sm text-gray-500 mt-1">A checkout was started for this location but not finished.</p>
                )}

                <div className="mt-4 flex flex-wrap gap-2">
                  {subscription.access?.open && !pendingCancel && (canChange || isExtraLocation) && (
                    <button
                      type="button"
                      onClick={() => setConfirming({ cancel: true })}
                      disabled={Boolean(busy)}
                      className="px-3 py-1.5 border border-red-300 text-red-700 rounded hover:bg-red-50 text-sm disabled:opacity-50"
                    >
                      {subscription.status === 'trial' ? 'Cancel trial' : 'Cancel plan'}
                    </button>
                  )}
                  {subscription.access?.open && pendingCancel && (subscription.live_subscription || !subscription.billing_live) && (
                    <button
                      type="button"
                      onClick={resumePlan}
                      disabled={Boolean(busy)}
                      className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm disabled:opacity-50"
                    >
                      {busy === 'resume' ? 'Saving...' : 'Keep my plan'}
                    </button>
                  )}
                  {subscription.billing_live && (subscription.stripe_subscription_id || subscription.live_subscription) && (
                    <button
                      type="button"
                      onClick={openPortal}
                      disabled={Boolean(busy)}
                      className="px-3 py-1.5 border border-gray-300 text-gray-800 rounded hover:bg-gray-50 text-sm disabled:opacity-50"
                    >
                      {busy === 'portal' ? 'Opening...' : 'Manage billing (card, invoices)'}
                    </button>
                  )}
                </div>

                {confirming?.cancel && (
                  <div className="mt-4 p-4 border border-red-200 bg-red-50 rounded">
                    <p className="text-sm text-red-800">
                      {subscription.status === 'trial'
                        ? `Cancel the free trial? You will not be charged. This location stays open until ${formatDate(subscription.trial_ends_at)}.`
                        : `Cancel this plan? It stays open until ${formatDate(subscription.current_period_end)} and is not renewed.`}
                    </p>
                    <div className="mt-3 flex gap-2">
                      <button
                        type="button"
                        onClick={cancelPlan}
                        disabled={Boolean(busy)}
                        className="px-3 py-1.5 bg-red-600 text-white rounded text-sm disabled:opacity-50"
                      >
                        {busy === 'cancel' ? 'Cancelling...' : 'Yes, cancel'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirming(null)}
                        className="px-3 py-1.5 border border-gray-300 rounded text-sm"
                      >
                        Keep it
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center gap-3">
              <span className="text-sm font-medium">Billing cycle:</span>
              {['monthly', 'annual'].map((cycle) => (
                <button
                  key={cycle}
                  type="button"
                  onClick={() => { setBillingCycle(cycle); setConfirming(null); }}
                  className={`px-3 py-1 rounded text-sm ${billingCycle === cycle ? 'bg-blue-600 text-white' : 'border'}`}
                >
                  {cycle === 'annual' ? 'Annual' : 'Monthly'}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {visiblePlanKeys.map((planKey) => {
                const plan = plansByKey[planKey];
                const price = billingCycle === 'annual' ? plan.annualPrice : plan.monthlyPrice;
                const isCurrent = hasOpenPlan && subscription?.plan_name === planKey;
                const highlighted = isCurrent || (!hasOpenPlan && suggestedPlan === planKey);
                const included = PLAN_INCLUDED[planKey] || ['The books for that location'];
                const button = planButton(planKey);
                const working = busy === `start-${planKey}` || busy === `change-${planKey}`;
                return (
                  <div key={planKey} className={`bg-white rounded shadow p-6 flex flex-col ${highlighted ? 'ring-2 ring-blue-500' : ''}`}>
                    <h3 className="text-lg font-semibold">{plan.name}</h3>
                    <p className="text-2xl font-bold mt-2">
                      {price ? `$${price}` : 'Not set'}
                      <span className="text-sm font-normal text-gray-500">
                        /{billingCycle === 'annual' ? 'year' : 'month'}
                      </span>
                    </p>
                    <ul className="mt-4 space-y-1 text-sm text-gray-700 list-disc pl-5 flex-1">
                      {included.map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                    <button
                      type="button"
                      onClick={button.onClick}
                      disabled={button.disabled || Boolean(busy)}
                      className="mt-4 w-full px-4 py-2 bg-blue-600 text-white rounded disabled:bg-gray-300"
                    >
                      {working ? 'Working...' : button.label}
                    </button>
                    {confirming?.planKey === planKey && (
                      <div className="mt-3 p-3 border border-blue-200 bg-blue-50 rounded text-sm">
                        <p className="text-blue-900">{confirming.text}</p>
                        <div className="mt-2 flex gap-2">
                          <button
                            type="button"
                            onClick={() => changePlan(planKey)}
                            disabled={Boolean(busy)}
                            className="px-3 py-1 bg-blue-600 text-white rounded disabled:opacity-50"
                          >
                            {busy === `change-${planKey}` ? 'Changing...' : 'Confirm'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirming(null)}
                            className="px-3 py-1 border border-gray-300 rounded"
                          >
                            Back
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default Billing;
